// ============================================================================
// Account Health Widget — top-line account counts + 5 most recent open
// facility issues so leadership can scan what's hot without leaving the page.
//
// "Recent issues" come from facility_feedback where status ∈ {Open, Recurring},
// joined to facilities + accounts for human-readable names. The hook does the
// join in memory rather than via a Postgrest embed because we already need
// both tables for other widgets — one round trip, four readouts.
// ============================================================================

import { Link } from 'react-router-dom';
import { CATEGORY_LABELS, SENTIMENT_STYLES } from '../../shared/design-tokens';
import type { AccountHealthSnapshot, RecentIssue } from './use-dashboard-data';
import { WidgetCard, WidgetSkeleton } from './widget-card';

interface Props {
  data: AccountHealthSnapshot | null;
  loading: boolean;
}

export function AccountHealthWidget({ data, loading }: Props) {
  if (loading && !data) {
    return (
      <WidgetCard title="Account health" minHeight={300}>
        <WidgetSkeleton lines={6} />
      </WidgetCard>
    );
  }

  if (!data) {
    return (
      <WidgetCard title="Account health" minHeight={300}>
        <p className="text-sm text-tcs-text-muted">No account data available.</p>
      </WidgetCard>
    );
  }

  return (
    <WidgetCard
      title="Account health"
      subtitle={`${data.total} accounts`}
      minHeight={300}
    >
      <div className="flex gap-4">
        <Stat label="Active" value={data.active} tone="ok" />
        <Stat label="Flagged" value={data.flagged} tone="warn" />
        <Stat label="Closed" value={data.closed} tone="muted" />
      </div>

      <div className="mt-4">
        <h3 className="mb-2 text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
          Recent open issues
        </h3>
        {data.recent_issues.length === 0 ? (
          <p className="text-sm text-tcs-text-muted">
            No open issues — every account is clear.
          </p>
        ) : (
          <ul className="divide-y divide-tcs-border">
            {data.recent_issues.map((issue) => (
              <IssueRow key={issue.feedback_id} issue={issue} />
            ))}
          </ul>
        )}
      </div>
    </WidgetCard>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'ok' | 'warn' | 'muted';
}) {
  const toneClass =
    tone === 'ok'
      ? 'text-st-ok-fg'
      : tone === 'warn'
        ? 'text-rag-red'
        : 'text-tcs-text-muted';
  return (
    <div>
      <div className={`text-2xl font-semibold tabular-nums ${toneClass}`}>
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-tcs-text-muted">
        {label}
      </div>
    </div>
  );
}

function IssueRow({ issue }: { issue: RecentIssue }) {
  // Defensive: if the DB ever drifts off the canonical 4-value enum
  // (Positive/Neutral/Negative/Critical), fall back to Neutral styling
  // rather than throwing on undefined.fill. A CHECK constraint on
  // facility_feedback.sentiment enforces the enum at the DB level — this
  // is belt-and-suspenders for the next time the schema and the data
  // disagree.
  const sentiment =
    SENTIMENT_STYLES[issue.sentiment] ?? SENTIMENT_STYLES.Neutral;
  const date = formatShortDate(issue.feedback_date);
  return (
    <li className="py-2 text-sm">
      <Link
        to={`/account-management/facilities/${issue.facility_id}`}
        className="block hover:bg-tcs-bg-muted hover:rounded -mx-1 px-1"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate font-medium text-tcs-text">
                {issue.facility_name ?? issue.facility_id}
              </span>
              {issue.account_name && (
                <span className="truncate text-xs text-tcs-text-muted">
                  · {issue.account_name}
                </span>
              )}
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-xs text-tcs-text-muted">
              <span className="rounded bg-tcs-surface px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-tcs-text-secondary">
                {CATEGORY_LABELS[issue.category]}
              </span>
              <span className="truncate" title={issue.description}>
                {truncate(issue.description, 70)}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-medium"
              style={{
                backgroundColor: sentiment.fill,
                color: sentiment.text,
              }}
            >
              {sentiment.label}
            </span>
            <span className="text-[10px] tabular-nums text-tcs-text-muted">
              {date}
            </span>
          </div>
        </div>
      </Link>
    </li>
  );
}

function formatShortDate(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const d = new Date(t);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${m}/${day}`;
}

function truncate(s: string, max: number): string {
  if (!s) return '';
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}
